// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CrossRateCalculationType, CrossRateType } from '@shared/utils/CrossRate/CrossRateCalculationType';
import { CrossRateDataValue } from '@shared/utils/CrossRate/CrossRateDataValue';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { CrossRateForPositions } from '@shared/utils/Enums/Constants';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { IsAllowed } from '../IsAllowed';
import { MathUtils } from '@shared/utils/MathUtils';
import { CrossRateKey } from './CrossRateKey';
export class CrossRateCache {
    constructor(dataCache) {
        this.CrossRateDict = new Map();
        this.OnUpdate = new CustomEvent();
        this.dataCache = dataCache;
    }
    // #endregion Constants
    Update(msg) {
        var _a;
        const crossRateDataArr = msg.CrossRateData;
        const len = crossRateDataArr.length;
        for (let i = 0; i < len; i++) {
            const crd = crossRateDataArr[i];
            const crossString = crd.Exp1 + CrossRateCache.CROSS_DELIMITER + crd.Exp2;
            let dataValue = this.CrossRateDict.get(crossString);
            if (isNullOrUndefined(dataValue)) {
                dataValue = new CrossRateDataValue();
            }
            if (!isNullOrUndefined(crd.IsReverse)) {
                dataValue.IsReverse = crd.IsReverse;
            }
            if (!isNullOrUndefined(crd.Price)) {
                dataValue.Price = crd.Price;
            }
            if (!isNullOrUndefined(crd.Bid)) {
                dataValue.Bid = crd.Bid;
            }
            if (!isNullOrUndefined(crd.Ask)) {
                dataValue.Ask = crd.Ask;
            }
            if (!isNullOrUndefined(crd.DefaultPriceType)) {
                dataValue.DefaultPriceType = crd.DefaultPriceType;
            }
            if (!isNullOrUndefined(crd.InstrumentId)) {
                dataValue.InstrumentId = crd.InstrumentId;
            }
            if (!isNullOrUndefined(crd.InstrumentTypeId)) {
                dataValue.InstrumentTypeId = crd.InstrumentTypeId;
            }
            if (!isNullOrUndefined(crd.PointSize)) {
                dataValue.PointSize = crd.PointSize;
            }
            const second = crd.SecondCrossRateData;
            if (!isNullOrUndefined(second)) {
                const secondCrossRateDataValue = (_a = dataValue.SecondCrossRateDataValue) !== null && _a !== void 0 ? _a : new CrossRateDataValue();
                if (!isNullOrUndefined(second.PointSize)) {
                    secondCrossRateDataValue.PointSize = second.PointSize;
                }
                if (!isNullOrUndefined(second.IsReverse)) {
                    secondCrossRateDataValue.IsReverse = second.IsReverse;
                }
                if (!isNullOrUndefined(second.Bid)) {
                    secondCrossRateDataValue.Bid = second.Bid;
                }
                if (!isNullOrUndefined(second.Ask)) {
                    secondCrossRateDataValue.Ask = second.Ask;
                }
                if (!isNullOrUndefined(second.InstrumentId)) {
                    secondCrossRateDataValue.InstrumentId = second.InstrumentId;
                }
                if (!isNullOrUndefined(second.InstrumentTypeId)) {
                    secondCrossRateDataValue.InstrumentTypeId = second.InstrumentTypeId;
                }
                dataValue.SecondCrossRateDataValue = secondCrossRateDataValue;
            }
            this.CrossRateDict.set(crossString, dataValue);
        }
        this.OnUpdate.Raise();
    }
    Clear() {
        this.CrossRateDict.clear();
        this.OnUpdate.Raise();
    }
    static FillCrossRateGraph(w, r) {
        // TODO. Workaround.
        if (!w.length)
            return;
        // Алгоритм Флойда — Уоршелла
        // (кубическая сложность, но можно улучшить)
        // немного адаптирован, так как у нас расстояние между вершинами определяется не суммированием, а умножением
        const len = w[0].length;
        for (let k = 0; k < len; k++) {
            for (let i = 0; i < len; i++) {
                for (let j = 0; j < len; j++) {
                    if (w[i][j] === 0 && w[i][k] !== 0 && w[k][j] !== 0) {
                        w[i][j] = w[i][k] * w[k][j];
                        r[i][j] = r[i][k] || r[k][j];
                    }
                }
            }
        }
    }
    GetCrossPriceInsSideExp2(acc, ins, side, reverseCrossString = false) {
        if (isNullOrUndefined(ins)) {
            return CrossRateCache.NO_CROSS_PRICE;
        }
        const crossKey = new CrossRateKey(ins.Exp2, acc.BaseCurrency);
        const useSwap = isNullOrUndefined(ins.CrossRateKey) || !crossKey.reverse().equals(ins.CrossRateKey);
        const crossString = reverseCrossString ? crossKey.reverse().toString() : crossKey.toString();
        const crosstype = this.GetCrossRateType(ins, side, crossString);
        const crossRateForPositionsType = ins.ValueCrossRateForPositions;
        return this.GetCrossRatesValue(crossString, crosstype, acc, useSwap, crossRateForPositionsType);
    }
    GetCrossPriceExp1Exp2(exp1, exp2, acc) {
        const crossKey = new CrossRateKey(exp1, exp2);
        const crossString = crossKey.toString();
        const crosstype = CrossRateCalculationType.BID_ASK_2;
        return this.GetCrossRatesValue(crossString, crosstype, acc);
    }
    GetCrossPriceExp1(exp1, acc) {
        const crossKey = new CrossRateKey(exp1, this.dataCache.baseCurrency);
        const crossString = crossKey.toString();
        const crosstype = CrossRateCalculationType.BID_ASK_2;
        return this.GetCrossRatesValue(crossString, crosstype, acc);
    }
    GetCrossPriceIns(acc, ins, side) {
        if (isNullOrUndefined(ins)) {
            return CrossRateCache.NO_CROSS_PRICE;
        }
        const crossKey = new CrossRateKey(ins.Exp2, this.dataCache.baseCurrency);
        const useSwap = isNullOrUndefined(ins.CrossRateKey) || !crossKey.reverse().equals(ins.CrossRateKey);
        const crossString = crossKey.toString();
        const crosstype = this.GetCrossRateType(ins, side, crossString);
        const crossRateForPositionsType = ins.ValueCrossRateForPositions;
        return this.GetCrossRatesValue(crossString, crosstype, acc, useSwap, crossRateForPositionsType);
    }
    GetNotEmptyCrossRatesStorage() {
        return this.CrossRateDict.size > 0;
    }
    GetNotEmptyCrossRatesValue(dataValue, crossType, account, useSwap, crossRateForPositionsType) {
        if (isNullOrUndefined(dataValue)) {
            return CrossRateCache.NO_CROSS_PRICE;
        }
        const [Bid, Ask] = this.ComputeFinalCrossPrice(account, dataValue, crossRateForPositionsType, useSwap);
        let result = NaN;
        switch (crossType) {
            case CrossRateCalculationType.BID:
                result = Bid;
                break;
            case CrossRateCalculationType.ASK:
                result = Ask;
                break;
            case CrossRateCalculationType.BID_ASK_2:
                result = !isNaN(Bid) && !isNaN(Ask) ? this.roundToServerPrecision((Bid + Ask) / 2) : NaN;
                break;
        }
        if (!isNaN(result)) {
            return result;
        }
        else if (!isNaN(dataValue.Price)) {
            return dataValue.Price;
        }
        else {
            return CrossRateCache.NO_CROSS_PRICE;
        }
    }
    ComputeFinalCrossPrice(account, dataValue, crossRateForPositionsType, useSwap) {
        const [bid1, ask1] = this.GetSpreadedAndReversed(account, dataValue, crossRateForPositionsType, useSwap);
        if (isNullOrUndefined(dataValue.SecondCrossRateDataValue)) {
            return [this.roundToServerPrecision(bid1), this.roundToServerPrecision(ask1)];
        }
        const [bid2, ask2] = this.GetSpreadedAndReversed(account, dataValue.SecondCrossRateDataValue, crossRateForPositionsType, useSwap);
        let finalBid = NaN;
        let finalAsk = NaN;
        if (!isNaN(bid1) && !isNaN(bid2)) {
            finalBid = bid1 * bid2;
        }
        if (!isNaN(ask1) && !isNaN(ask2)) {
            finalAsk = ask1 * ask2;
        }
        return [this.roundToServerPrecision(finalBid), this.roundToServerPrecision(finalAsk)];
    }
    GetSpreadedAndReversed(account, dataValue, crossRateForPositionsType, useSwap) {
        var _a, _b;
        if (crossRateForPositionsType !== CrossRateForPositions.LatestBidAsk) {
            useSwap = false;
        }
        const spreadItem = this.getSpreadItem(dataValue, account);
        const spreadedBid = (_a = spreadItem === null || spreadItem === void 0 ? void 0 : spreadItem.CalcBid(dataValue.Bid, dataValue.Ask, null, true, dataValue.PointSize)) !== null && _a !== void 0 ? _a : dataValue.Bid;
        const spreadedAsk = (_b = spreadItem === null || spreadItem === void 0 ? void 0 : spreadItem.CalcAsk(dataValue.Bid, dataValue.Ask, null, true, dataValue.PointSize)) !== null && _b !== void 0 ? _b : dataValue.Ask;
        if (dataValue.IsReverse === CrossRateType.Reverse) {
            const reverseBid = 1.0 / (useSwap ? spreadedAsk : spreadedBid);
            const reverseAsk = 1.0 / (useSwap ? spreadedBid : spreadedAsk);
            return [reverseBid, reverseAsk];
        }
        return [spreadedBid, spreadedAsk];
    }
    GetCrossRatesValue(crossString, crosstype, account, useSwap = true, crossRateForPositionsType = CrossRateForPositions.Default) {
        if (this.GetNotEmptyCrossRatesStorage()) {
            const dataValue = this.CrossRateDict.get(crossString);
            return this.GetNotEmptyCrossRatesValue(dataValue, crosstype, account, useSwap, crossRateForPositionsType);
        }
    }
    GetCrossRateType(instrument, side, crossString) {
        if (instrument.ValueCrossRateForPositions === CrossRateForPositions.LatestBidAsk) {
            if (side === OperationType.Buy) {
                return CrossRateCalculationType.BID;
            }
            else {
                return CrossRateCalculationType.ASK;
            }
        }
        else if (this.GetNotEmptyCrossRatesStorage()) {
            const dataValue = this.CrossRateDict.get(crossString);
            if (!isNullOrUndefined(dataValue)) {
                return dataValue.DefaultPriceType;
            }
            else {
                return CrossRateCalculationType.BID_ASK_2;
            }
        }
        else {
            return CrossRateCalculationType.BID_ASK_2;
        }
    }
    getSpreadItem(dataValue, account) {
        if (!IsAllowed.IsUseSpreadPlanForCrossRates()) {
            return null;
        }
        const spreadPlan = this.dataCache.GetSpreadPlan(account);
        if (isNullOrUndefined(spreadPlan)) {
            return null;
        }
        return spreadPlan.GetSpreadItem(dataValue.InstrumentId, dataValue.InstrumentTypeId);
    }
    roundToServerPrecision(value) {
        return MathUtils.RoundToIncrement(value, 0.00000001);
    }
}
// #region Constants
CrossRateCache.NO_CROSS_PRICE = 1;
CrossRateCache.CROSS_DELIMITER = '/';
